// src/hospital/shared/hospitalShared.jsx
// Shared design tokens, reusable components and hooks for all Hospital pages
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────
export const C = {
  primary:   '#0f4c81',
  accent:    '#00b4d8',
  success:   '#10b981',
  warning:   '#f59e0b',
  danger:    '#ef4444',
  purple:    '#8b5cf6',
  bg:        '#f0f4f8',
  surface:   '#ffffff',
  border:    '#e2e8f0',
  text:      '#1e293b',
  textSoft:  '#64748b',
  textMuted: '#94a3b8',
};

// ─────────────────────────────────────────────────────────────────────────────
// SHARED STYLES
// ─────────────────────────────────────────────────────────────────────────────
export const S = {
  page: { padding: 0, fontFamily: "'DM Sans', system-ui, sans-serif", background: C.bg, minHeight: '100vh' },
  card: { background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20, backdropFilter: 'blur(4px)' },
  modalBox: { background: '#fff', borderRadius: 16, width: '100%', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.22)' },
  modalHead: { padding: '18px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, background: 'linear-gradient(135deg,#f8fafc,#f1f5f9)' },
  modalFoot: { padding: '14px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0, background: '#fafbfc' },
  scrollBody: { padding: 24, overflowY: 'auto', flex: 1 },
  // Buttons
  btnPri:   { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 20px', background: `linear-gradient(135deg,${C.primary},#1a6bb5)`, color: '#fff', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSec:   { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 20px', background: '#fff', color: C.text, border: `1px solid ${C.border}`, borderRadius: 9, fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnDng:   { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 20px', background: '#fef2f2', color: C.danger, border: '1px solid #fecaca', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSuc:   { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 20px', background: '#f0fdf4', color: C.success, border: '1px solid #bbf7d0', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: 'transparent', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 500, color: C.textSoft },
  // Form
  label:  { display: 'block', fontSize: 12, fontWeight: 700, color: C.textSoft, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' },
  input:  { width: '100%', padding: '10px 13px', border: `1.5px solid ${C.border}`, borderRadius: 9, fontSize: 14, outline: 'none', color: C.text, background: '#fff', boxSizing: 'border-box', transition: 'border-color .15s' },
  select: { width: '100%', padding: '10px 13px', border: `1.5px solid ${C.border}`, borderRadius: 9, fontSize: 14, outline: 'none', color: C.text, background: '#fff', cursor: 'pointer', boxSizing: 'border-box' },
  textarea:{ width: '100%', padding: '10px 13px', border: `1.5px solid ${C.border}`, borderRadius: 9, fontSize: 14, outline: 'none', color: C.text, background: '#fff', boxSizing: 'border-box', minHeight: 80, resize: 'vertical', fontFamily: 'inherit' },
  // Table
  table:  { width: '100%', borderCollapse: 'collapse' },
  th:     { padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMuted, borderBottom: `1px solid ${C.border}`, background: '#fafbfc', textTransform: 'uppercase', letterSpacing: '0.07em' },
  td:     { padding: '13px 16px', borderBottom: `1px solid #f8fafc`, fontSize: 13, verticalAlign: 'middle', color: C.text },
  // Misc
  errBox:  { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '11px 16px', marginBottom: 16 },
  center:  { padding: 48, textAlign: 'center', color: C.textMuted, fontSize: 14 },
  divider: { height: 1, background: C.border, margin: '18px 0' },
  secTitle:{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 },
};

// ─────────────────────────────────────────────────────────────────────────────
// REUSABLE COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

export const Badge = ({ color = C.text, bg = '#f3f4f6', children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color, background: bg }}>{children}</span>
);

export const StatusBadge = ({ status }) => {
  const map = {
    Active:        { color: '#166534', bg: '#dcfce7' },
    Inactive:      { color: '#475569', bg: '#f1f5f9' },
    Pending:       { color: '#92400e', bg: '#fef3c7' },
    Admitted:      { color: '#1e40af', bg: '#dbeafe' },
    Discharged:    { color: '#166534', bg: '#dcfce7' },
    Scheduled:     { color: '#6b21a8', bg: '#f3e8ff' },
    Completed:     { color: '#166534', bg: '#dcfce7' },
    Cancelled:     { color: '#991b1b', bg: '#fee2e2' },
    Available:     { color: '#166534', bg: '#dcfce7' },
    Occupied:      { color: '#1e40af', bg: '#dbeafe' },
    Maintenance:   { color: '#92400e', bg: '#fef3c7' },
    Critical:      { color: '#991b1b', bg: '#fee2e2' },
    Stable:        { color: '#166534', bg: '#dcfce7' },
    'Under Review':{ color: '#92400e', bg: '#fef3c7' },
    Paid:          { color: '#166534', bg: '#dcfce7' },
    Unpaid:        { color: '#991b1b', bg: '#fee2e2' },
    'Partially Paid':{ color: '#92400e', bg: '#fef3c7' },
  };
  const s = map[status] || { color: '#374151', bg: '#f3f4f6' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: s.color, background: s.bg }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
      {status}
    </span>
  );
};

export const FormField = ({ label, children, hint, col = 1 }) => (
  <div style={{ gridColumn: col > 1 ? `span ${col}` : undefined }}>
    <label style={S.label}>{label}</label>
    {children}
    {hint && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>{hint}</div>}
  </div>
);

export const ErrList = ({ errors }) => !errors?.length ? null : (
  <div style={S.errBox}>
    {errors.map((e, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, color: C.danger, fontSize: 13, marginBottom: i < errors.length - 1 ? 4 : 0 }}>
        ⚠ {e}
      </div>
    ))}
  </div>
);

export const Spinner = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
    <div style={{ width: 36, height: 36, border: `3px solid ${C.border}`, borderTop: `3px solid ${C.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

export const EmptyState = ({ icon: Icon, title, sub, action }) => (
  <div style={{ padding: '56px 20px', textAlign: 'center' }}>
    {Icon && <Icon size={48} color={C.textMuted} style={{ marginBottom: 14, opacity: 0.4 }} />}
    <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>{title}</div>
    {sub && <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 18 }}>{sub}</div>}
    {action}
  </div>
);

export const StatCard = ({ label, value, color = C.primary, bg = '#eff6ff', Icon }) => (
  <div style={{ background: bg, borderRadius: 12, padding: '16px 18px', border: `1px solid ${color}20` }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.textSoft, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      {Icon && <Icon size={18} color={color} />}
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
  </div>
);

// ── Page header ───────────────────────────────────────────────────────────────
export const PageHeader = ({ title, subtitle, Icon, color = C.primary, action }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      {Icon && (
        <div style={{ width: 46, height: 46, borderRadius: 12, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={22} color={color} />
        </div>
      )}
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.02em' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 13, color: C.textSoft, margin: '3px 0 0' }}>{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

// ── Search + filter bar ───────────────────────────────────────────────────────
export const FilterBar = ({ search, onSearch, filters = [], children }) => (
  <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
    <div style={{ flex: 1, minWidth: 220, position: 'relative' }}>
      <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.textMuted, fontSize: 14 }}>🔍</span>
      <input type="text" placeholder="Search..." value={search} onChange={e => onSearch(e.target.value)}
        style={{ ...S.input, paddingLeft: 36, fontSize: 13 }} />
    </div>
    {filters.map(f => (
      <select key={f.key} value={f.value} onChange={e => f.onChange(e.target.value)}
        style={{ ...S.select, width: 'auto', fontSize: 13, padding: '9px 14px' }}>
        {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ))}
    {children}
  </div>
);

// ── Data table with pagination ────────────────────────────────────────────────
export const DataTable = ({ columns, data, loading, onRowClick, emptyTitle, emptyIcon, page, totalPages, onPageChange, total }) => {
  if (loading) return <Spinner />;
  if (!data?.length) return <EmptyState icon={emptyIcon} title={emptyTitle || 'No records found'} />;

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table style={S.table}>
          <thead>
            <tr>{columns.map(c => <th key={c.key || c.label} style={S.th}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={row._id || row.id || i}
                style={{ cursor: onRowClick ? 'pointer' : 'default', transition: 'background .12s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => onRowClick?.(row)}>
                {columns.map(c => (
                  <td key={c.key || c.label} style={S.td}>
                    {c.render ? c.render(row[c.key], row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 12, color: C.textMuted }}>{total} records</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={{ ...S.btnSec, padding: '6px 14px', fontSize: 12 }} disabled={page <= 1} onClick={() => onPageChange(page - 1)}>Prev</button>
            <span style={{ padding: '6px 12px', fontSize: 12, color: C.textSoft }}>{page} / {totalPages}</span>
            <button style={{ ...S.btnSec, padding: '6px 14px', fontSize: 12 }} disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Next</button>
          </div>
        </div>
      )}
    </>
  );
};

// ── Toast ────────────────────────────────────────────────────────────────────
export const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);
  const ToastEl = toast ? (
    <div style={{ position: 'fixed', top: 18, right: 18, zIndex: 9999, padding: '12px 20px', borderRadius: 10, background: toast.type === 'error' ? '#fee2e2' : '#f0fdf4', color: toast.type === 'error' ? C.danger : C.success, fontWeight: 600, fontSize: 13, border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 8, minWidth: 240 }}>
      {toast.type === 'error' ? '❌' : '✅'} {toast.msg}
    </div>
  ) : null;
  return { show, ToastEl };
};

// ── Confirm dialog ────────────────────────────────────────────────────────────
export const useConfirm = () => {
  const [state, setState] = useState(null);
  const confirm = (msg) => new Promise(resolve => setState({ msg, resolve }));
  const Dialog = state ? (
    <div style={S.modalOverlay} onClick={() => { state.resolve(false); setState(null); }}>
      <div style={{ ...S.modalBox, maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '28px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 8 }}>Confirm Action</div>
          <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 24 }}>{state.msg}</div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button style={S.btnSec} onClick={() => { state.resolve(false); setState(null); }}>Cancel</button>
            <button style={S.btnDng} onClick={() => { state.resolve(true); setState(null); }}>Confirm</button>
          </div>
        </div>
      </div>
    </div>
  ) : null;
  return { confirm, Dialog };
};

// ── Modal wrapper ─────────────────────────────────────────────────────────────
export const Modal = ({ title, subtitle, onClose, children, footer, maxWidth = 640 }) => (
  <div style={S.modalOverlay} onClick={onClose}>
    <div style={{ ...S.modalBox, maxWidth }} onClick={e => e.stopPropagation()}>
      <div style={S.modalHead}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: C.text, margin: 0 }}>{title}</h2>
          {subtitle && <div style={{ fontSize: 12, color: C.textSoft, marginTop: 2 }}>{subtitle}</div>}
        </div>
        <button style={S.btnGhost} onClick={onClose}>✕</button>
      </div>
      <div style={S.scrollBody}>{children}</div>
      {footer && <div style={S.modalFoot}>{footer}</div>}
    </div>
  </div>
);

// ── 2-col form grid ────────────────────────────────────────────────────────────
export const FormGrid = ({ children, cols = 2 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, gap: 14, marginBottom: 20 }}>{children}</div>
);

export default { C, S, Badge, StatusBadge, FormField, ErrList, Spinner, EmptyState, StatCard, PageHeader, FilterBar, DataTable, Modal, FormGrid, useToast, useConfirm };